/**
 * /site/:siteId - one site.
 *
 * The page opens on a summary strip and the map. The full record - every value
 * with the source it came from - sits behind the "Site record" disclosure, shut
 * on every load. A value the registry could not resolve is not given a row.
 */

import { useEffect, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useParams } from 'react-router-dom'
import { ChevronDown } from 'lucide-react'

import { api } from '../api/client'
import { SitesMap, hazardColour, hazardLabel, storage, type MapSite } from '../components/SitesMap'
import { useAppStore } from '../store/useAppStore'

interface Quantity {
  value: unknown
  unit?: string
  source?: string | null
  status?: string
  reason?: string | null
}
interface SiteDetail {
  id: string
  display_name: string
  hazard_class: string
  structure_types: string[]
  india_region: string
  crs: string
  state: string | null
  river: string | null
  center: { lat: number; lon: number; source: string }
  state_detail: Quantity
  district: Quantity
  river_detail: Quantity
  dem: Quantity
  eac_table: Quantity
  reservoir_volume: Quantity
  dem_available: boolean
  run_dir: string | null
  levels: Record<string, Quantity>
  settlements: { name?: string }[]
  hecras_scenarios: string[]
  dss_keys: string[]
  notes: string
}

const has = (q: Quantity | undefined) => q !== undefined && q.value !== null && q.value !== undefined && q.value !== ''

function RecordRow({ label, q }: { label: string; q: Quantity }) {
  if (!has(q)) return null
  const numeric = typeof q.value === 'number'
  const shown = numeric ? (q.value as number).toLocaleString('en-IN') : String(q.value)
  return (
    <tr>
      <td className="whitespace-nowrap text-slate-300">{label}</td>
      <td className={numeric ? 'num whitespace-nowrap' : 'whitespace-nowrap text-slate-100'}>
        {shown}
        {q.unit ? ` ${q.unit.replace('m3', 'm³')}` : ''}
      </td>
      <td className="text-slate-500">{q.source ?? ''}</td>
    </tr>
  )
}

function Tile({ label, value }: { label: string; value: string | null }) {
  if (!value) return null
  return (
    <div className="metric-tile">
      <span className="section-label">{label}</span>
      <span className="text-20 font-medium leading-7 text-slate-100">{value}</span>
    </div>
  )
}

export default function SiteDashboard() {
  const { siteId = '' } = useParams()
  const setActive = useAppStore((s) => s.setActiveSiteId)
  const [recordOpen, setRecordOpen] = useState(false)

  useEffect(() => {
    if (siteId) setActive(siteId)
  }, [siteId, setActive])
  // Shut on every load, and on every change of site.
  useEffect(() => setRecordOpen(false), [siteId])

  const detail = useQuery<SiteDetail>({ queryKey: ['site', siteId], queryFn: () => api.raw(`/sites/${siteId}`) })
  const sites = useQuery<{ run_site: string }>({ queryKey: ['sites'], queryFn: () => api.raw('/sites') })

  if (detail.isPending) return <div className="skeleton m-4 h-[calc(100%-2rem)]" />
  if (detail.isError || !detail.data) return <p className="p-6 text-14 text-rose-400">{String(detail.error)}</p>

  const d = detail.data
  const hasResults = Boolean(d.run_dir) || sites.data?.run_site === d.id
  const volume = typeof d.reservoir_volume.value === 'number' ? (d.reservoir_volume.value as number) : null
  const mapSite: MapSite = {
    id: d.id,
    display_name: d.display_name,
    state: d.state,
    river: d.river,
    hazard_class: d.hazard_class,
    lat: d.center.lat,
    lon: d.center.lon,
    volume_m3: volume,
    has_results: hasResults,
  }
  const levels = Object.entries(d.levels).filter(([, q]) => has(q))

  return (
    <div className="flex h-full min-h-0 flex-col" data-testid="site-dashboard">
      <div className="shrink-0 px-4 pt-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h1 className="page-title flex items-center gap-2.5">
            <span
              className="inline-block h-3 w-3 rounded-full"
              style={{ background: hazardColour(d.hazard_class) }}
              aria-hidden
            />
            {d.display_name}
          </h1>
          <button
            type="button"
            className="ctl flex items-center gap-2"
            aria-expanded={recordOpen}
            aria-controls="site-record"
            onClick={() => setRecordOpen((v) => !v)}
          >
            Site record
            <ChevronDown
              className={`h-4 w-4 transition-transform duration-150 ${recordOpen ? 'rotate-180' : ''}`}
              aria-hidden
            />
          </button>
        </div>

        <div className="mt-3 grid gap-2 sm:grid-cols-2 xl:grid-cols-5">
          <Tile label="Hazard" value={hazardLabel(d.hazard_class)} />
          <Tile label="State" value={d.state} />
          <Tile label="River" value={d.river} />
          <Tile label="Storage" value={volume ? storage(volume) : null} />
          <Tile label="Coordinate system" value={d.crs} />
        </div>
      </div>

      {recordOpen && (
        <div id="site-record" className="min-h-0 shrink-0 overflow-y-auto px-4 pt-3" style={{ maxHeight: '52%' }}>
          <div className="grid gap-3 xl:grid-cols-2">
            <section className="panel !p-0">
              <header className="panel-header">
                <h2>Record</h2>
              </header>
              <div className="overflow-x-auto">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Field</th>
                      <th>Value</th>
                      <th>Source</th>
                    </tr>
                  </thead>
                  <tbody>
                    <RecordRow label="State" q={d.state_detail} />
                    <RecordRow label="District" q={d.district} />
                    <RecordRow label="River" q={d.river_detail} />
                    <RecordRow label="Terrain" q={d.dem} />
                    <RecordRow label="Storage curve" q={d.eac_table} />
                    {volume !== null && (
                      <RecordRow
                        label="Reservoir volume"
                        q={{ value: storage(volume), source: d.reservoir_volume.source }}
                      />
                    )}
                    <RecordRow
                      label="Centre"
                      q={{ value: `${d.center.lat.toFixed(4)}, ${d.center.lon.toFixed(4)}`, source: d.center.source }}
                    />
                    {d.structure_types.length > 0 && (
                      <RecordRow label="Structures" q={{ value: d.structure_types.join(', '), source: '' }} />
                    )}
                    {d.hecras_scenarios.length > 0 && (
                      <RecordRow label="HEC-RAS plans" q={{ value: d.hecras_scenarios.join(', '), source: '' }} />
                    )}
                    {d.dss_keys.length > 0 && (
                      <RecordRow label="DSS series" q={{ value: d.dss_keys.join(', '), source: '' }} />
                    )}
                    {d.settlements.length > 0 && (
                      <RecordRow
                        label="Settlements"
                        q={{ value: d.settlements.map((s) => s.name).filter(Boolean).join(', '), source: '' }}
                      />
                    )}
                  </tbody>
                </table>
              </div>
              {d.notes && <p className="border-t border-slate-800 p-4 text-14 leading-6 text-slate-400">{d.notes}</p>}
            </section>

            {levels.length > 0 && (
              <section className="panel !p-0">
                <header className="panel-header">
                  <h2>Levels</h2>
                  <span className="section-label">metres</span>
                </header>
                <div className="overflow-x-auto">
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>Level</th>
                        <th className="num">Elevation</th>
                        <th>Source</th>
                      </tr>
                    </thead>
                    <tbody>
                      {levels.map(([key, q]) => (
                        <tr key={key}>
                          <td className="whitespace-nowrap capitalize text-slate-300">
                            {key.replace(/_m$/, '').replace(/_/g, ' ')}
                          </td>
                          <td className="num">{Number(q.value).toFixed(2)}</td>
                          <td className="text-slate-500">{q.source ?? ''}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </section>
            )}
          </div>
        </div>
      )}

      <div className="mt-3 min-h-0 flex-1 border-t border-slate-800">
        <SitesMap sites={[mapSite]} activeSiteId={d.id} focusState={d.state} legend={false} />
      </div>
    </div>
  )
}
